/**
 * support_chat_controller.js — one support conversation, Messenger style
 * (Stimulus identifier support-chat).
 *
 * Bound to templates/components/support_thread.html, the one copy of the
 * thread markup. Two modes:
 *   * customer: the signed-in user's own ongoing chat (the widget's Support
 *     pane). support-inbox hands it the GET /api/support/me it already made
 *     (loadCustomer); otherwise it loads that itself once visible.
 *   * staff: one customer's thread, loaded when showConversation(id) is called
 *     by the admin thread list or inbox; or a new, empty one to a customer
 *     who has none (startConversation), created by its first send.
 *
 * Messages arrive three ways and are merged by id, so overlap is harmless:
 * the load, live 'chippie:support' document events (sse-connect.js /
 * realtime-hub.js re-dispatch the Mercure user topic), and a catch-up read
 * (?after=<last id>) whenever the thread becomes visible, the tab is
 * refocused, or a send fails. Live delivery is fire-and-forget; the catch-up
 * is what keeps a page with no live connection current.
 *
 * Sends show a pending bubble at once and swap it for the server's message
 * ('support-chat:sent' then fires, so a staff header can show the claim);
 * a failure leaves "Not sent, tap to retry" with the server's reason. Photos
 * are shrunk by photo-downscale on the file input, whose validity is the lock
 * while it works; the server owns the size and type limits.
 *
 * Raising a ticket dispatches 'support-chat:ticket' for the feedback form
 * controller to open with the conversation's ticket URL; the resulting system
 * line comes back as an ordinary message. Pure logic: _support_messages.js.
 */
import { Controller } from "@hotwired/stimulus";
import { autosizeTextarea } from "./_autosize.js";
import { supportRequest } from "./_support_api.js";
import {
    escapeHtml, groupMessages, mergeMessages, pastedImage, postPath, readUpToId, renderBody,
    renderMessageBody, seenMessageId, ticketPrefill, typingLabel,
} from "./_support_messages.js";

const SCROLL_PIN_PX = 48;   // same stick-to-bottom rule as the Chip AI transcript
const TYPING_SHOWN_MS = 4000;
const TYPING_SENT_EVERY_MS = 3000;
const READ_DEBOUNCE_MS = 500;

export default class extends Controller {
    static targets = ['scroller', 'list', 'typing', 'input', 'file', 'preview', 'ticketAction'];
    static values = { mode: String, me: Number, api: String };

    connect() {
        this.messages = this.messages || [];
        this.seen = this.seen || {};
        this._onVisibility = () => { if (document.visibilityState === 'visible') this.catchUp(); };
        document.addEventListener('visibilitychange', this._onVisibility);
        this._onViewport = () => { if (this._pinned()) this._scrollToBottom(); };
        window.visualViewport?.addEventListener('resize', this._onViewport);
        // After the render that pinned the view, photos still load (the list
        // grows) and the staff header gains its tickets (the scroller shrinks);
        // a view that was at the bottom stays there.
        this._onScroll = () => this._scrolled();
        this.scrollerTarget.addEventListener('scroll', this._onScroll, { passive: true });
        this._resizeObserver = new ResizeObserver(() => { if (this._stuck) this._scrollToBottom(); });
        this._resizeObserver.observe(this.scrollerTarget);
        this._resizeObserver.observe(this.listTarget);
        this._observer = new IntersectionObserver((entries) => this._visibilityChanged(entries));
        this._observer.observe(this.listTarget);
    }

    disconnect() {
        document.removeEventListener('visibilitychange', this._onVisibility);
        window.visualViewport?.removeEventListener('resize', this._onViewport);
        this.scrollerTarget.removeEventListener('scroll', this._onScroll);
        this._resizeObserver.disconnect();
        this._observer.disconnect();
        clearTimeout(this._typingTimer);
        clearTimeout(this._readTimer);
        this._revokePreview();
    }

    // ==================== Loading ====================

    // A thread opened before repaints at once from what it showed last; the
    // fetch then merges what changed. Only a first open shows the skeleton.
    async showConversation(conversationId) {
        this._reset(conversationId, null);
        this.listTarget.classList.toggle('support-messages--loading', !this.messages.length);
        try {
            const data = await this._get(`/conversations/${conversationId}`);
            this._load(data.messages, data.seen);
            this.dispatch('loaded', { detail: data });
        } finally {
            this.listTarget.classList.remove('support-messages--loading');
        }
    }

    /**
     * Staff: an empty thread to a customer who has none yet. The first send
     * creates it (POST /customers/<id>/messages) and the thread carries on as
     * any other.
     */
    startConversation(customerUserId) {
        this._reset(null, customerUserId);
    }

    // Switching threads keeps the one left in memory (the staff view reuses
    // this instance for every thread), keyed by conversation id.
    _reset(conversationId, recipientId) {
        this._shown = this._shown || new Map();
        if (this.conversationId) this._shown.set(this.conversationId, this.messages);
        this.conversationId = conversationId;
        this._recipientId = recipientId;
        this.messages = this._shown.get(conversationId) || [];
        this._readSent = 0;
        this._hideTyping();
        this._render(true);
    }

    // Best effort: a failed catch-up is retried on the next trigger.
    async catchUp() {
        if (!this._visible) return;
        try {
            if (!this.conversationId) {
                if (this.modeValue === 'customer') await this._loadCustomer();
                return;
            }
            const saved = this.messages.filter((m) => typeof m.id === 'number');
            const after = saved.length ? `?after=${saved[saved.length - 1].id}` : '';
            const data = await this._get(`/conversations/${this.conversationId}/messages${after}`);
            this.seen = data.seen || this.seen;
            this._merge(data.messages);
        } catch (_error) {
            /* offline or signed out: nothing to merge */
        }
    }

    async _loadCustomer() {
        this.loadCustomer(await this._get('/me'));
    }

    /** Show the customer's chat from a GET /api/support/me response (also fed by support-inbox). */
    loadCustomer(data) {
        this._ownChat = true;
        this.conversationId = data.conversation_id;
        this._load(data.messages, data.seen);
    }

    _load(messages, seen) {
        this.seen = seen || {};
        this.messages = mergeMessages(this.messages, messages);
        this._render(true);
        this._markReadSoon();
    }

    _visibilityChanged(entries) {
        this._visible = entries.some((entry) => entry.isIntersecting);
        if (!this._visible) return;
        this.catchUp();
        this._markReadSoon();
    }

    // ==================== Live events ====================

    // data-action="chippie:support@document->support-chat#receive"
    receive({ detail }) {
        const data = detail?.data || {};
        if (detail?.type === 'support_message' && this._accepts(data)) {
            this.conversationId = data.conversation_id;
            this._hideTyping();
            this._merge([data]);
        } else if (data.conversation_id !== this.conversationId) {
            return;
        } else if (detail.type === 'support_typing') {
            this._showTyping(data.first_name);
        } else if (detail.type === 'support_read') {
            const key = data.is_staff ? 'staff_read_id' : 'customer_read_id';
            this.seen = { ...this.seen, [key]: Math.max(this.seen[key] || 0, data.message_id) };
            this._render(false);
        }
    }

    // Before the customer's thread exists, the first message on it is theirs
    // whoever wrote it (their own, or staff starting the conversation): a
    // customer hears only their own thread. The chat must be known to be the
    // customer's (loadCustomer): staff pages hold a hidden customer-mode chat
    // too, and staff hear every thread.
    _accepts(message) {
        if (this.conversationId) return message.conversation_id === this.conversationId;
        return this.modeValue === 'customer' && Boolean(this._ownChat);
    }

    _merge(messages) {
        if (!messages.length) return;
        this.messages = mergeMessages(this.messages, messages);
        this._render(false);
        this._markReadSoon();
    }

    // ==================== Sending ====================

    // data-action="keydown->support-chat#keydown": Enter sends, Shift+Enter is a newline.
    keydown(event) {
        if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
        event.preventDefault();
        this.send();
    }

    send() {
        const body = this.inputTarget.value.trim();
        const image = this.fileTarget.files[0] || null;
        if (!body && !image) return;
        if (image && !this.fileTarget.checkValidity()) {
            this.fileTarget.reportValidity();
            return;
        }
        const pending = {
            id: `pending-${Date.now()}`, kind: image ? 'image' : 'text', body: body || null,
            sender_user_id: this.meValue, is_staff_sender: this.modeValue === 'staff',
            created_at: new Date().toISOString(), _image: image,
            _previewUrl: image ? URL.createObjectURL(image) : null,
        };
        this.inputTarget.value = '';
        this._autosize();
        this.clearImage();
        this.messages = mergeMessages(this.messages, [pending]);
        this._render(true);
        this._post(pending);
    }

    // data-action="click->support-chat#retry" on a failed bubble.
    retry({ params }) {
        const pending = this.messages.find((m) => m.id === params.id);
        if (!pending) return;
        pending._failed = null;
        this._render(false);
        this._post(pending);
    }

    async _post(pending) {
        const path = postPath(this.modeValue, this.conversationId, this._recipientId);
        try {
            const saved = await this._request('POST', path, this._messageBody(pending));
            if (pending._previewUrl) URL.revokeObjectURL(pending._previewUrl);
            this.conversationId = saved.conversation_id;
            this.messages = mergeMessages(this.messages.filter((m) => m !== pending), [saved]);
            this._render(true);
            this.dispatch('sent', { detail: { conversationId: this.conversationId, message: saved } });
        } catch (error) {
            pending._failed = error.message;
            this._render(false);
            this.catchUp();
        }
    }

    _messageBody(pending) {
        if (!pending._image) return JSON.stringify({ body: pending.body });
        const form = new FormData();
        form.append('image', pending._image);
        if (pending.body) form.append('body', pending.body);
        return form;
    }

    // data-action="input->support-chat#typing"
    typing() {
        this._autosize();
        const now = Date.now();
        if (!this.conversationId || now - (this._typingSentAt || 0) < TYPING_SENT_EVERY_MS) return;
        this._typingSentAt = now;
        this._request('POST', `/conversations/${this.conversationId}/typing`).catch(() => {});
    }

    pickImage() {
        this.fileTarget.click();
    }

    // data-action="paste->support-chat#paste" on the message box. A pasted image
    // goes into the file input as if picked, so photo-downscale shrinks it and
    // filePicked previews it exactly as for the picture button.
    paste(event) {
        const image = pastedImage(event.clipboardData);
        if (!image) return;
        event.preventDefault();
        const transfer = new DataTransfer();
        transfer.items.add(image);
        this.fileTarget.files = transfer.files;
        this.fileTarget.dispatchEvent(new Event('change'));
    }

    // data-action="change->support-chat#filePicked" on the file input.
    filePicked() {
        const image = this.fileTarget.files[0];
        this._revokePreview();
        this.previewTarget.hidden = !image;
        if (!image) return;
        this._previewUrl = URL.createObjectURL(image);
        this.previewTarget.innerHTML = `<img src="${this._previewUrl}" alt="Photo to send">
            <button type="button" class="support-preview-remove" aria-label="Remove photo"
                    data-action="click->support-chat#clearImage"><i class="bi bi-x-lg"></i></button>`;
    }

    clearImage() {
        this.fileTarget.value = '';
        this.previewTarget.hidden = true;
        this.previewTarget.innerHTML = '';
        this._revokePreview();
    }

    // The composer preview's object URL holds the picked file in memory until revoked.
    _revokePreview() {
        if (this._previewUrl) URL.revokeObjectURL(this._previewUrl);
        this._previewUrl = null;
    }

    // ==================== Read, typing, tickets ====================

    _markReadSoon() {
        clearTimeout(this._readTimer);
        this._readTimer = setTimeout(() => this._markRead(), READ_DEBOUNCE_MS);
    }

    async _markRead() {
        if (!this._showing() || !this.conversationId) return;
        const newest = readUpToId(this.messages);
        if (!newest || newest <= (this._readSent || 0)) return;
        this._readSent = newest;
        try {
            await this._request('POST', `/conversations/${this.conversationId}/read`,
                JSON.stringify({ message_id: newest }));
        } catch (_error) {
            this._readSent = 0;   // unsent: the next render tries again
            return;
        }
        this.dispatch('read', { detail: { conversationId: this.conversationId } });
    }

    _showTyping(firstName) {
        this.typingTarget.textContent = typingLabel(firstName);
        this.typingTarget.hidden = false;
        clearTimeout(this._typingTimer);
        this._typingTimer = setTimeout(() => this._hideTyping(), TYPING_SHOWN_MS);
    }

    _hideTyping() {
        clearTimeout(this._typingTimer);
        this.typingTarget.hidden = true;
    }

    // data-action="click->support-chat#raiseTicket", with data-support-chat-id-param on a message.
    raiseTicket({ params }) {
        const message = this.messages.find((m) => m.id === params.id);
        const prefill = message ? ticketPrefill(message) : { title: '', description: '' };
        this.dispatch('ticket', { detail: {
            url: `${this.apiValue}/conversations/${this.conversationId}/tickets`,
            sourceMessageId: message ? message.id : null, ...prefill,
        } });
    }

    // ==================== Rendering ====================

    _render(forceBottom) {
        const pinned = forceBottom || this._pinned();
        const images = new Map([...this.listTarget.querySelectorAll('img[data-message-id]')]
            .map((img) => [img.dataset.messageId, img]));
        const seenId = seenMessageId(this.messages, this.modeValue, this.meValue,
            this.seen[this.modeValue === 'staff' ? 'customer_read_id' : 'staff_read_id']);
        this.listTarget.innerHTML = groupMessages(this.messages, this.modeValue, this.meValue)
            .map((item) => this._itemHtml(item, seenId)).join('');
        // Keep loaded photos rather than fetching them again on every render.
        this.listTarget.querySelectorAll('img[data-message-id]').forEach((img) => {
            const kept = images.get(img.dataset.messageId);
            if (kept && kept.src === img.src) img.replaceWith(kept);
        });
        if (this.hasTicketActionTarget) this.ticketActionTarget.hidden = !this.conversationId;
        if (pinned) this._scrollToBottom();
    }

    _itemHtml(item, seenId) {
        if (item.type === 'day') return `<div class="support-day">${item.label}</div>`;
        if (item.type === 'system') {
            return `<div class="support-system">${renderBody(item.message.body)}</div>`;
        }
        const first = item.messages[0];
        const label = first.is_staff_sender && first.sender_user_id !== this.meValue
            ? `<div class="support-sender"><span class="support-initial">${renderBody(first.sender_initial)}</span>${renderBody(first.sender_first_name)}</div>`
            : '';
        const bubbles = item.messages.map((m) => this._bubbleHtml(m, item.own)
            + (m.id === seenId ? '<div class="support-seen">Seen</div>' : '')).join('');
        return `<div class="support-run support-run--${item.own ? 'own' : 'other'}">${label}${bubbles}</div>`;
    }

    _bubbleHtml(message, own) {
        const image = this._imageHtml(message);
        const body = message.body ? `<div>${renderMessageBody(message)}</div>` : '';
        const failed = this._failedHtml(message);
        const side = own ? 'pchat-msg--user' : 'pchat-msg--assistant';
        const photoOnly = image && !body && !failed ? ' support-msg--photo' : '';
        return `<div class="support-bubble-row"><div class="pchat-msg ${side} support-msg${photoOnly}">${image}${body}${failed}</div>${this._ticketHtml(message)}</div>`;
    }

    _imageHtml(message) {
        const src = escapeHtml(message.attachment_url || message._previewUrl || '');
        if (!src) return '';
        return `<a href="${src}" data-action="photo-viewer#open:prevent"
            data-photo-viewer-url-param="${src}"><img class="support-img"
            src="${src}" alt="Photo" loading="lazy" data-message-id="${message.id}"></a>`;
    }

    _failedHtml(message) {
        if (message._failed === undefined || message._failed === null) return '';
        return `<button type="button" class="support-retry" data-action="click->support-chat#retry"
            data-support-chat-id-param="${message.id}">Not sent, tap to retry.
            ${renderBody(message._failed)}</button>`;
    }

    _ticketHtml(message) {
        if (typeof message.id !== 'number') return '';
        return `<button type="button" class="support-msg-ticket" title="Raise a ticket from this message"
            aria-label="Raise a ticket from this message" data-action="click->support-chat#raiseTicket"
            data-support-chat-id-param="${message.id}"><i class="bi bi-flag"></i></button>`;
    }

    _pinned() {
        const el = this.scrollerTarget;
        return el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_PIN_PX;
    }

    _scrollToBottom() {
        this.scrollerTarget.scrollTop = this.scrollerTarget.scrollHeight;
        this._stuck = true;
    }

    // Only the reader scrolling up unpins the view. A scroll event is
    // dispatched a frame after it happens, by when a photo may have grown the
    // list under a jump to the bottom; measured then, that jump would read as
    // leaving the bottom.
    _scrolled() {
        const top = this.scrollerTarget.scrollTop;
        if (top < this._lastTop) this._stuck = this._pinned();
        else if (this._pinned()) this._stuck = true;
        this._lastTop = top;
    }

    _autosize() {
        autosizeTextarea(this.inputTarget);
    }

    // Seen by a person: the thread is shown and the browser tab is in front.
    _showing() {
        return this._visible && document.visibilityState === 'visible';
    }

    // ==================== HTTP ====================

    _get(path) {
        return this._request('GET', path);
    }

    _request(method, path, body) {
        return supportRequest(this.apiValue, method, path, body);
    }
}
